import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import {
  ConsultationsConsumerRenderProps,
  withConsultationsContext
} from 'components/consultations/ConsultationsContext';
import { CheckboxSc } from 'components/final-form/CheckBox';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { MandateConsumerRenderProps, withMandateContext } from 'components/MandateContext';
import MediDataDownloadDetailsModal from 'components/medidata-download/MediDataDownloadDetailsModal';
import UpsertPatient from 'components/patient/upsert/UpsertPatient';
import PdfModal from 'components/pdf-modal/PdfModal';
import { TreatmentMode } from 'components/treatments/TreatmentsContext';
import TreatmentsForm from 'components/treatments/TreatmentsForm';
import moment from 'moment';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Checkbox, Icon, Loader, Modal } from 'semantic-ui-react';
import { getAllCountries } from 'services/countryServices';
import axios from 'services/http';
import { getAllLanguages } from 'services/languageServices';
import {
  archiveRejectedBillsAndRecreateXml,
  getRejectedBillsPdfUrl,
  stornoRejectedBills
} from 'services/mediDataServices';
import { findPatientById } from 'services/patientServices';
import { getTreatmentById } from 'services/treatmentServices';
import styled from 'styled-components';
import { BillStatus, Privilege } from 'ts-types/api.enums';
import {
  CountryDto,
  LanguageDto,
  StornoRejectedBillsResponseDto,
  UpsertPatientDto,
  UpsertTreatmentDto,
  XmlResponseRequestDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { noop } from 'utils/functionUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const TableDiv = styled.div`
  flex: 1 1 auto;
  padding: 0.5rem;
  background: white;
`;

const ActionsDiv = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  padding: 0.5rem;
  background: white;
`;

const SendErrorDiv = styled.div`
  flex: 1 1 auto;
  margin-left: 1.25rem;
  color: darkred;
`;

const NoDataDiv = styled.div`
  font-size: 1rem;
  color: #444;
  font-style: italic;
  padding: 0.7rem 1rem;
`;

const StyledRightAlignCell = styled.div`
  text-align: right;
  padding-right: 0.4rem;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.bodyColumn {
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
    white-space: normal;
  }

  & .evenRow:hover, .oddRow:hover {
    background: rgba(0, 0, 0, .05);
  }

  .edit-icon {
    padding-left: 8px;
  }

  .error-row {

    .error-code {
      color: darkred;
      padding-right: 1rem;
    }
  }
`;

enum AllChosenState {
  ALL,
  SOME,
  NONE
}

export interface EditTreatmentValues extends UpsertTreatmentDto {
  id: number
}

interface Props extends MessageSourceProps,
    AuthConsumerRenderProps,
    RouteComponentProps,
    MandateConsumerRenderProps,
    ConsultationsConsumerRenderProps {

  xmlResponses: Array<XmlResponseRequestDto>;
  tariffId: number;
  responsesLoading: boolean;
  refreshResponses: () => void;
}

interface State {
  mandateId: number,
  xmlDetailsResponse?: XmlResponseRequestDto,

  responses: Array<XmlResponseViewModel>,
  detailsModalOpen: boolean,
  allChosenState: AllChosenState,

  sendError?: string,

  cancelTokenSource: CancelTokenSource

  stornoRejectedBillsPdfData?: StornoRejectedBillsResponseDto,
  stornoRejectedBillsModalOpen: boolean,
  patient?: UpsertPatientDto,
  patientId?: number,
  countries: Array<CountryDto>,
  languages: Array<LanguageDto>,
  treatment?: EditTreatmentValues,
  editPatientModalOpen: boolean,
  editTreatmentModalOpen: boolean
}

interface XmlResponseViewModel extends XmlResponseRequestDto {
  checked: boolean
}

class MediDataDownloadTableView extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const {currentUser} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const mappedResponses: Array<XmlResponseViewModel> = this.props.xmlResponses.map(xmlResponse => {

      return {
        ...xmlResponse,
        checked: false
      }
    });

    getAllCountries(cancelTokenSource).then(response => {
      this.setState({
        countries: response
      });
    }).catch(noop);

    getAllLanguages(cancelTokenSource).then(response => {
      this.setState({
        languages: response
      });
    }).catch(noop);

    this.state = {
      mandateId: currentUser!.userConfig.mandateId,
      responses: mappedResponses,
      detailsModalOpen: false,
      cancelTokenSource,
      allChosenState: AllChosenState.NONE,
      stornoRejectedBillsModalOpen: false,
      editPatientModalOpen: false,
      editTreatmentModalOpen: false,
      countries: [],
      languages: []
    }
  }

  componentDidUpdate(
      {xmlResponses: xmlResponses}: Readonly<Props>,
      prevState: Readonly<State>,
      snapshot?: any
  ): void {

    if (this.props.xmlResponses !== xmlResponses) {
      const mappedResponses: Array<XmlResponseViewModel> = this.props.xmlResponses.map(response => {
        return {...response, checked: false};
      });

      this.setState({responses: mappedResponses});
    }

    if (this.state.sendError) {
      this.clearSendErrorMessage();
    }
  }

  clearSendErrorMessage = (): void => {
    setTimeout(() => {
      this.setState({
        sendError: undefined
      })
    }, 5000);
  }

  showDetailsModal = (rowData: XmlResponseRequestDto) => {

    this.setState(() => ({
      xmlDetailsResponse: rowData,
      detailsModalOpen: true
    }));
  };

  hideDetailsModal = () => {
    this.setState(() => ({
      detailsModalOpen: false
    }));
  };

  openEditPatientModal = (patientId?: number) => {
    const {cancelTokenSource} = this.state;

    if (patientId) {
      findPatientById(cancelTokenSource, patientId)
          .then(response => {
            this.setState({
              patient: response,
              patientId: patientId
            })
            this.toggleEditPatientModal();
          })
          .catch(noop)
    }
  };

  toggleEditPatientModal = () => {
    this.setState(({editPatientModalOpen}: State) => ({
      editPatientModalOpen: !editPatientModalOpen
    }));
  }

  openEditTreatmentModal = (treatmentId: number, mode?: TreatmentMode) => {

    const {cancelTokenSource} = this.state;
    if (treatmentId) {

      getTreatmentById(treatmentId, cancelTokenSource)
          .then(response => {
            this.setState({
              treatment: {
                ...response,
                id: treatmentId,
                costEstimationId: response.costEstimationId ? response.costEstimationId : -1,
                readOnly: mode === TreatmentMode.DETAILS
              },
              patientId: response.patientId
            })
            this.toggleEditTreatmentModal();
          })
          .catch(noop)
    }
  }

  toggleEditTreatmentModal = () => {
    this.setState(({editTreatmentModalOpen}: State) => ({
      editTreatmentModalOpen: !editTreatmentModalOpen
    }));
  }

  onPatientSave = () => {
    this.toggleEditPatientModal();
  };

  getPdfUrl = (stornoRejectedBillsResponse: StornoRejectedBillsResponseDto) => {
    return getRejectedBillsPdfUrl(stornoRejectedBillsResponse.pdfId, stornoRejectedBillsResponse.pdfName);
  }

  openRejectedBillsPdfModal = (stornoRejectedBillsResponse: StornoRejectedBillsResponseDto) => {
    this.setState({
      stornoRejectedBillsModalOpen: true,
      stornoRejectedBillsPdfData: stornoRejectedBillsResponse
    })
  };

  closeStornoRejectedBillsPdfModal = () => {

    this.setState({
      stornoRejectedBillsModalOpen: false,
      stornoRejectedBillsPdfData: undefined
    });
  };

  printXmlResponses = () => {
    this.stornoSelectedBills(true, false);
  }

  stornoXmlResponses = () => {
    this.stornoSelectedBills(false, false);
  }

  archiveXmlResponses = () => {
    this.stornoSelectedBills(false, true);
  }

  stornoSelectedBills = async (preview: boolean, archiveOnly: boolean) => {

    const {tariffId} = this.props;
    const {mandateId, responses, cancelTokenSource} = this.state;

    const billXmlIds = responses
        .filter(xmlResponse => (xmlResponse.checked && xmlResponse.patientId !== ""))
        .map(xmlResponse => xmlResponse.billId);

    this.setState({
      sendError: undefined
    });

    await stornoRejectedBills(mandateId, tariffId, billXmlIds, preview, archiveOnly, cancelTokenSource)
        .then(response => {

          if (!response.preview) {
            const newBills: Array<XmlResponseViewModel> = response.xmlResponseRequestDtos.map(xmlResponse => {

              return {
                ...xmlResponse,
                checked: false
              };
            });

            this.setState({
              responses: newBills,
              allChosenState: AllChosenState.NONE
            });

          }

          this.openRejectedBillsPdfModal(response);
        })
        .catch(error => {
          this.handleSendError(error.response.data);
        });
  };

  archiveAndRecreateXmlForSelectedBills = async () => {

    const {tariffId, refreshResponses} = this.props;
    const {mandateId, responses, cancelTokenSource} = this.state;

    const billXmlIds = responses
        .filter(xmlResponse => xmlResponse.checked)
        .map(xmlResponse => xmlResponse.billXmlResponseId);

    this.setState({
      sendError: undefined
    });

    await archiveRejectedBillsAndRecreateXml(mandateId, tariffId, billXmlIds, cancelTokenSource)
        .then(response => {
          refreshResponses();
        })
        .catch(error => {
          this.handleSendError(error.response.data);
        });
  }

  handleSendError(error: any) {
    const {getMessage} = this.props;

    const errorCode = error.errorCode;
    const knownErrors = [errorUtils.noMandateTariffConfig];
    if (knownErrors.includes(errorCode)) {
      this.setState({
        sendError: getMessage(`mediDataDownload.error.${errorCode}`)
      });
    } else {
      this.setState({
        sendError: getMessage('error.general')
      })
    }
  }

  render(): React.ReactNode {
    const {
      getMessage,
      responsesLoading,
      currentMandates,
      getAllMandates,
      currentConsultations,
      currentUserHasPrivilege
    } = this.props;
    const {
      stornoRejectedBillsModalOpen, stornoRejectedBillsPdfData, responses, allChosenState, sendError,
      editPatientModalOpen, editTreatmentModalOpen, treatment, patient, patientId, countries, languages
    } = this.state;
    if (responsesLoading) {
      return this.renderLoader();
    }

    const hasResponses = responses && responses.length > 0;
    const hasArchiveRejectedBillsAndRecreateXmlPrivilege = currentUserHasPrivilege(
        Privilege.PX_CORE_ARCHIVE_REJECTED_BILL_AND_RECREATE_XML);

    const selectedResponses = hasResponses ? responses.filter(res => res.checked) : [];

    const disableButtons = !(hasResponses && allChosenState !== AllChosenState.NONE);

    // check if all of them that are selected are invalid
    const areAllInvalid = selectedResponses.filter(sr => sr.checked && sr.billId && sr.patientId != "").length == 0;

    return (
        <>
          <TableDiv>
            {hasResponses ?
                (this.renderTable(responses)) : (<NoDataDiv>{getMessage("mediDataDownload.noXmlResponses")}</NoDataDiv>)
            }
          </TableDiv>
          <ActionsDiv>
          <Button
              compact
              type="buton"
              primary
              disabled={disableButtons || areAllInvalid}
              onClick={this.printXmlResponses}
          >
            {getMessage("mediDataDownload.preview")}
          </Button>
          <Button
              compact
              type="buton"
              primary
              disabled={disableButtons}
              onClick={this.archiveXmlResponses}
          >
            {getMessage("mediDataDownload.archiveOnly")}
          </Button>
          <Button
              compact
              type="submit"
              primary
              disabled={disableButtons || areAllInvalid}
              onClick={this.stornoXmlResponses}
          >
            {getMessage("mediDataDownload.storno")}
          </Button>
            {
              hasResponses && hasArchiveRejectedBillsAndRecreateXmlPrivilege &&
                <>
                  <Button compact type="button" onClick={this.selectNoQrCodeResponses}>
                    Select No QR code responses
                  </Button>

                  <Button compact type="button" onClick={this.archiveAndRecreateXmlForSelectedBills}>
                    Archive response & Recreate XML
                  </Button>
                </>
            }
            {
                stornoRejectedBillsModalOpen &&
                <PdfModal
                    modalOpen={stornoRejectedBillsModalOpen}
                    onCloseModal={this.closeStornoRejectedBillsPdfModal}
                    pdfResponses={[stornoRejectedBillsPdfData]}
                    getPdfUrl={this.getPdfUrl}
                />
            }
            {sendError && (
                <SendErrorDiv>
                  {sendError}
                </SendErrorDiv>
            )}
          </ActionsDiv>
          {this.renderDetailsModal()}
          {
              editPatientModalOpen &&
              <UpsertPatient
                  modalOpen={editPatientModalOpen}
                  toggleModal={this.toggleEditPatientModal}
                  countries={countries}
                  languages={languages}
                  patient={patient}
                  patientId={patientId}
                  onPatientSave={this.onPatientSave}
                  currentMandates={currentMandates}
                  getAllMandates={getAllMandates}
              />
          }
          {
              editTreatmentModalOpen &&
              <TreatmentsForm
                  treatmentFormOpen={editTreatmentModalOpen}
                  toggleUpsertModal={this.toggleEditTreatmentModal}
                  treatment={treatment}
                  currentPatientId={patientId}
                  currentConsultations={currentConsultations}
              />
          }
        </>
    );
  }

  renderTable = (xmlResponses: Array<XmlResponseViewModel>): JSX.Element => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.responseRowGetter()}
            rowRenderer={this.responseRowRenderer()}
            rowCount={xmlResponses.length}
            rowHeight={76}
            columns={[
              {
                width: 120,
                flexGrow: 0,
                flexShrink: 0,
                label: this.allChosenHeaderColumn(xmlResponses),
                dataKey: "checked",
                cellRenderer: this.checkedCellRenderer
              },
              {
                width: 100,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediDataDownload.billStatus"),
                dataKey: "billStatus",
                cellRenderer: this.billStatusCellRenderer
              },
              {
                width: 70,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediDataDownload.patientId"),
                dataKey: "patientId",
                cellRenderer: this.patientIdCellRenderer
              },

              {
                width: 260,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediDataDownload.fileName"),
                dataKey: "fileName"
              },
              {
                width: 80,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediDataDownload.responseDate"),
                dataKey: "responseDate",
                cellRenderer: this.responseDateCellRenderer
              },
              {
                width: 80,
                flexGrow: 0,
                flexShrink: 0,
                label: getMessage("mediDataDownload.responseType"),
                dataKey: "status",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 180,
                flexGrow: 1,
                flexShrink: 0,
                label: getMessage("mediDataDownload.companyInfo"),
                dataKey: "companyContact",
                cellRenderer: this.companyInfoCellRenderer
              },
              {
                width: 180,
                flexGrow: 1,
                flexShrink: 0,
                label: getMessage("mediDataDownload.contactInfo"),
                dataKey: "companyContact",
                cellRenderer: this.contactInfoCellRenderer
              },
              {
                width: 400,
                flexGrow: 3,
                flexShrink: 0,
                label: getMessage("mediDataDownload.reason"),
                dataKey: "reason",
                cellRenderer: this.reasonCellRenderer
              },
              {
                width: 30,
                flexGrow: 1,
                flexShrink: 0,
                label: getMessage("mediDataDownload.numberOfErrors"),
                dataKey: "errors",
                cellRenderer: this.numberOfErrorsCellRenderer
              }

            ]}
        />
    )
  };

  allChosenHeaderColumn = (responses: Array<XmlResponseViewModel>) => {

    const allChosenState = this.state.allChosenState;

    const countBills = responses.length;
    const countSelectedBills = responses.filter(b => b.checked).length;

    return (
        <CheckboxSc
            checked={allChosenState === AllChosenState.ALL}
            indeterminate={allChosenState === AllChosenState.SOME}
            onChange={
              () => {
                const currentState = this.state.allChosenState;
                let nextState = AllChosenState.NONE;
                let responses: Array<XmlResponseViewModel>;
                if (currentState === AllChosenState.ALL || currentState === AllChosenState.SOME) {
                  nextState = AllChosenState.NONE;
                  responses = this.state.responses!.map(xmlResponse => ({...xmlResponse, checked: false}));
                } else {
                  nextState = AllChosenState.ALL;
                  responses = this.state.responses!.map(xmlResponse => {
                    const xmlValid = xmlResponse.xmlValid;
                    const status = xmlResponse.status;
                    return {...xmlResponse, checked: this.xmlResponseChoosable(xmlValid, status)}
                  });
                }

                this.setState({
                  allChosenState: nextState,
                  responses
                })
              }
            }
            label={`${countSelectedBills} / ${countBills}`}
            disabled={countBills === 0}
        />
    )
  };

  responseRowGetter = () => ({index}: any) => {

    const {responses: xmlResponses} = this.state;

    if (xmlResponses[index]) {
      Object.assign(xmlResponses[index], {index: index + 1});

      return xmlResponses[index];
    }

    return {
      index: index + 1
    }
  };

  responseRowRenderer = () =>
      ({className, columns, index, key, style}: any) => {

        const a11yProps = {"aria-rowindex": index + 1};

        let styles = {...style};

        const {responses} = this.state;

        const billId = responses[index].billId;
        const patientId = Number(responses[index].patientId);

        const xmlValid = responses[index].xmlValid;
        const status = responses[index].status;
        const checkEnabled = this.xmlResponseChoosable(xmlValid, status);

        return (
            <div
                {...a11yProps}
                className={className}
                key={key}
                role="row"
                style={styles}
                onDoubleClick={() => {
                  if (checkEnabled) {
                    this.toggleXmlResponse(billId)
                  }
                }}
            >
              {columns}
            </div>
        );
      };

  checkedCellRenderer = (data: any) => {

    const {getMessage, currentUser} = this.props;
    const {rowData} = data;

    const xmlValid = rowData.xmlValid;
    const status = rowData.status;
    const treatmentId = rowData.treatmentId;
    const checkEnabled = this.xmlResponseChoosable(xmlValid, status);

    const shouldDisableTreatmentEdit = (BillStatus.OPEN != rowData.billStatus)
        || (currentUser && currentUser.userConfig.mandateId != rowData.mandateId);
    const editIconName = shouldDisableTreatmentEdit ? 'info circle' : 'edit outline';
    const editIconTitle = shouldDisableTreatmentEdit
        ? getMessage("mediDataDownload.reviewTreatment.button.title")
        : getMessage("mediDataDownload.editTreatment.button.title");

    return (
        <React.Fragment>
          <Checkbox
              checked={rowData.checked}
              disabled={!checkEnabled}
              label={rowData.billId}
              onClick={() => {
                if (checkEnabled) {
                  this.toggleXmlResponse(rowData.billId)
                }
              }}
          />
          <Icon name={'warning sign'} color={'red'} onClick={() => this.showDetailsModal(rowData)}/>
          {treatmentId &&
              <Icon
                  name={editIconName}
                  title={editIconTitle}
                  onClick={() => {
                    if (shouldDisableTreatmentEdit) {
                      this.openEditTreatmentModal(treatmentId, TreatmentMode.DETAILS)
                    } else {
                      this.openEditTreatmentModal(treatmentId)
                    }
                  }}
                  className="edit-icon"
              />
          }
        </React.Fragment>
    )
  };

  billStatusCellRenderer = (data: any) => {

    const {rowData} = data;

    return (<div>{rowData.billStatus}</div>)
  };

  patientIdCellRenderer = (data: any) => {

    const {getMessage} = this.props;

    const {rowData} = data;
    const patientId = rowData.patientId;

    return (<StyledRightAlignCell>
      {patientId}
      {patientId &&
          <Icon
              name={'edit outline'}
              title={getMessage("mediDataDownload.editPatient.button.title")}
              onClick={() => this.openEditPatientModal(patientId)}
              className="edit-icon"
          />
      }
    </StyledRightAlignCell>)
  };

  statusCellRenderer = (data: any) => {

    const {rowData} = data;

    return (<div>{rowData.status}</div>)
  };

  responseDateCellRenderer = (data: any) => {

    const {rowData} = data;

    return (<div>{moment(rowData.responseDate).format("DD.MM.YYYY")}</div>)
  };

  companyInfoCellRenderer = (data: any) => {
    const {rowData} = data;

    const companyContact = rowData.companyContact;
    const companyInfoDisplay = companyContact ? companyContact.companyInfoHtml : "";
    return (
        <div title={companyInfoDisplay}>{companyInfoDisplay}</div>)
  };

  contactInfoCellRenderer = (data: any) => {

    const {rowData} = data;

    const companyContact = rowData.companyContact;
    const contactInfoDisplay = companyContact ? companyContact.contactInfoHtml : "";
    return (<div title={contactInfoDisplay}>{contactInfoDisplay}</div>)
  };

  reasonCellRenderer = (data: any) => {

    const {rowData} = data;

    const hasErrors = rowData.errors && rowData.errors.length > 0;
    if (rowData.reason) {
      let errorsRow = <></>;
      if (hasErrors) {
        errorsRow = <div className="error-row">
          {rowData.errors.map((error: any) => {
            return <span className="error-code" title={error.text}>{error.code}</span>;
          })}
        </div>;
      }
      return <div>
        {errorsRow}
        <div title={rowData.reason}>{rowData.reason}</div>
      </div>;
    }

    if (hasErrors) {
      return <div>
        {rowData.errors.map((error: any) => {
          return <div className="error-row">
            <span className="error-code">{error.code}</span>
            <span className="error-text">{error.text}</span>
          </div>;
        })}
      </div>;
    }
  };

  numberOfErrorsCellRenderer = (data: any) => {

    const {rowData} = data;

    return (<StyledRightAlignCell>{rowData.errors.length == 0 ? "" : rowData.errors.length}</StyledRightAlignCell>)
  };

  renderLoader = (): JSX.Element => {

    const {getMessage} = this.props;
    return (
        <Loader active={true} size="small">
          {`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}
        </Loader>
    )
  };

  toggleXmlResponse = (billId: string) => {
    const stateBills: Array<XmlResponseViewModel> = this.state.responses;
    const responses = stateBills.map(
        xmlResponse => (xmlResponse.billId !== billId) ?
            xmlResponse : {...xmlResponse, checked: !xmlResponse.checked});

    const chosenXmlResponse = responses.find(xmlResponse => xmlResponse.checked);
    const unchosenXmlResponse = responses.find(xmlResponse => !xmlResponse.checked);

    let allChosenState = AllChosenState.SOME;
    if (!unchosenXmlResponse) {
      allChosenState = AllChosenState.ALL;
    } else if (!chosenXmlResponse) {
      allChosenState = AllChosenState.NONE;
    }

    this.setState({
      responses,
      allChosenState
    });
  };

  selectNoQrCodeResponses = () => {
    const stateBills: Array<XmlResponseViewModel> = this.state.responses;
    const responses = stateBills.map(xmlResponse => {

      let qrError = false;

      const errors = xmlResponse.errors;
      if (errors && errors.length === 1) {

        const error = errors[0];
        if (error.code === "939"
            && error.text.startsWith("Zahlungsinformation: Format des Einzahlungsscheins wird nicht mehr")) {
          qrError = true;
        } else if (error.code === "RW_003902"
            && error.text.startsWith("Ab dem 01.10.2022 sind Zahlungen an ein ESR")) {
          qrError = true;
        } else if (error.code === "VUT"
            && error.text.startsWith("Ab 1. September 2022 akzeptiert die CONCORDIA")) {
          qrError = true;
        } else if (error.code === "101240"
            && error.text.startsWith("Ab 1. Oktober 2022 sind Zahlungen an ein ESR-")) {
          qrError = true;
        } else if (error.code === "546774"
            && error.text.startsWith("Auszahlung nur noch via QR-Rechnung möglich, bitte korrigierte Rechnung übermitteln")) {
          qrError = true;
        } else if (error.code === "eK5.1.7"
            && error.text.startsWith("Ungültige ESR-Teilnehmer Nummer, bitte mit QR-IBAN abrechnen")) {
          qrError = true;
        } else if (error.code === "4"
            && error.text.startsWith("Die Zahlungsinformationen sind fehlerhaft. Bitte stellen Sie uns eine QR-Rechnung zu.")) {
          qrError = true;
        } else if (error.code === "99.1.1"
            && error.text.startsWith("Rechnungen die einen Einzahlungsschein mit Referenznummer (ESR) enthalten")) {
          qrError = true;
        } else if (error.code === "eK1.9.1"
            && error.text.startsWith("Wir bitten Sie um Zustellung einer QR-Rechnung")) {
          qrError = true;
        } else if (error.code === "S34"
            && error.text.startsWith("Wir bitten Sie, uns die Rechnung erneut mit einer gültigen Kontoverbindung")) {
          qrError = true;
        }
      } else if (errors && errors.length === 2) {
        const error1 = errors[0];
        const error2 = errors[1];
        if (error1.code === "939"
            && error1.text.startsWith("Zahlungsinformation: Format des Einzahlungsscheins wird nicht mehr unterstützt")
            && error2.code === "939"
            && error2.text.startsWith("Zahlungsinformation: Format des Einzahlungsscheins wird nicht mehr unterstützt")) {
          qrError = true;
        }
      } else if (!errors || errors.length === 0 && xmlResponse.reason === "XML hat keine QR-Informationen.") {
        qrError = true;
      }

      if (xmlResponse.billStatus !== "BILL") {
        qrError = false;
      }

      if (!this.xmlResponseChoosable(xmlResponse.xmlValid, xmlResponse.status)) {
        qrError = false;
      }

      return {
        ...xmlResponse,
        checked: qrError
      };
    });

    const chosenXmlResponse = responses.find(xmlResponse => xmlResponse.checked);
    const unchosenXmlResponse = responses.find(xmlResponse => !xmlResponse.checked);

    let allChosenState = AllChosenState.SOME;
    if (!unchosenXmlResponse) {
      allChosenState = AllChosenState.ALL;
    } else if (!chosenXmlResponse) {
      allChosenState = AllChosenState.NONE;
    }

    this.setState({
      responses,
      allChosenState
    });
  };

  xmlResponseChoosable(xmlValid: boolean, status: string): boolean {
    return xmlValid && status === 'REJECTED';
  }

  renderDetailsModal = () => {
    const {tariffId, getMessage} = this.props;
    const {detailsModalOpen, xmlDetailsResponse, mandateId} = this.state;

    if (!detailsModalOpen) {
      return <></>;
    }

    return (
        <Modal
            open={detailsModalOpen}
            onClose={() => this.hideDetailsModal()}
            closeOnDimmerClick={true}
        >
          <Modal.Header>{getMessage("mediDataDownload.details")}</Modal.Header>
          <FormModalContentContainer>
            <MediDataDownloadDetailsModal
                tariffId={tariffId}
                mandateId={mandateId}
                xmlDetailsResponse={xmlDetailsResponse}
                onCancel={this.hideDetailsModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  }
}

export default withMessages(withAuthContext(withRouterWorkaround(withMandateContext(
    withConsultationsContext(MediDataDownloadTableView)))));
